// Physical Count V2 quantity normalization (RCG-1).
//
// Fixes TC-PCV2-FRM-007 (cleared field must become 0), TC-PCV2-FRM-009
// (thousands separator "1,000" must parse to 1000), and the same class of
// failure surfaced by TC-PCV2-FRM-003 (add-then-Update) and TC-PCV2-IMP-024
// (re-import). Without this, the frontend forwards raw grid values ("",
// "1,000", NaN) straight into the backend payload, where DocumentLine's
// `validates :actual_quantity, numericality: true` rejects them with the
// generic toast "Document Line: actual quantity is not a number".
//
// Rules (whole-of-count semantics):
//   - null / undefined / blank / whitespace-only  -> 0  (a cleared count IS 0)
//   - numbers                                      -> the number (NaN/Infinity -> 0)
//   - strings                                      -> trimmed, thousands separators
//                                                     stripped, then Number()
//   - anything non-finite after parsing            -> 0  (defensive; field-level
//                                                     guards reject bad input earlier)
//
// Pure and side-effect free so it can be unit tested without a browser.

export function normalizePhysicalCountV2Quantity(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).trim().replace(/,/g, '');
  if (cleaned === '') return 0;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
}

// Normalizes the `quantity` and `actual_quantity` fields on every line in an
// array, returning new line objects (does not mutate the input). Only the two
// numeric quantity fields are touched; all other fields pass through untouched.
export function normalizePhysicalCountV2Lines(lines) {
  if (!Array.isArray(lines)) return lines;
  return lines.map((line) => {
    if (!line || typeof line !== 'object') return line;
    const normalized = { ...line };
    if ('quantity' in normalized) {
      normalized.quantity = normalizePhysicalCountV2Quantity(normalized.quantity);
    }
    if ('actual_quantity' in normalized) {
      normalized.actual_quantity = normalizePhysicalCountV2Quantity(normalized.actual_quantity);
    }
    return normalized;
  });
}

// CNC-002: project a PCount line to only the fields a user can actually edit,
// so the "unsaved changes" dirty check ignores grid-computed columns
// (total_gross, price_php, design "" vs null, product_status, ...) that never
// exist in the originally-loaded server data. Comparing these projections
// avoids the guard being permanently true on an untouched, freshly-loaded doc.
export function physicalCountV2EditableProjection(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => ({
      line_no: Number(r.line_no),
      product_id: r.product_id,
      quantity: normalizePhysicalCountV2Quantity(r.quantity),
      actual_quantity: normalizePhysicalCountV2Quantity(r.actual_quantity),
      remarks: r.remarks == null ? '' : String(r.remarks),
      document_line_locations: r.document_line_locations || [],
      document_line_serials_text: r.document_line_serials_text == null ? '' : String(r.document_line_serials_text),
    }))
    .sort((a, b) => a.line_no - b.line_no);
}

// True when the editable projection of the current grid differs from the
// originally-loaded lines (i.e. the user genuinely changed something).
export function physicalCountV2LinesDirty(origLines, currentLines) {
  return JSON.stringify(physicalCountV2EditableProjection(origLines)) !==
    JSON.stringify(physicalCountV2EditableProjection(currentLines));
}

// CNC-002: the message a losing session must see when another user froze (or
// otherwise mutated) the same document first. Kept here as the single source of
// truth so the backend conflict code and the frontend both agree on the text.
export const PHYSICAL_COUNT_V2_STALE_MESSAGE =
  'This document was updated by another user. Please reload the document.';

// CNC-002: the machine-readable code the freeze endpoint returns when the
// client's known updated_at no longer matches the row it locked FOR UPDATE
// (i.e. another session committed first). The frontend keys off this code so a
// genuine server-side conflict is never confused with a local-dirty state.
export const PHYSICAL_COUNT_V2_STALE_CODE = 'STALE_DOCUMENT';

// CNC-002: true when a freeze/action error response represents a server-side
// "updated by another user" concurrency conflict (as opposed to a validation
// error or a local-dirty guard). The backend sends { error_code } alongside the
// human message; we also fall back to matching the canonical message text so an
// older/looser backend response still maps correctly.
export function physicalCountV2IsStaleConflict(response) {
  if (!response || typeof response !== 'object') return false;
  if (response.error_code === PHYSICAL_COUNT_V2_STALE_CODE) return true;
  const errors = response.error;
  const list = Array.isArray(errors) ? errors : (errors == null ? [] : [errors]);
  return list.some((e) => String(e).includes(PHYSICAL_COUNT_V2_STALE_MESSAGE));
}

// CNC-002: given the raw freeze response, return the list of messages to show.
// A stale conflict always collapses to the single canonical reload message so
// the losing session is told to reload rather than "you have unsaved changes".
export function physicalCountV2FreezeErrorMessages(response) {
  if (physicalCountV2IsStaleConflict(response)) {
    return [PHYSICAL_COUNT_V2_STALE_MESSAGE];
  }
  const errors = (response && response.error) || [];
  if (Array.isArray(errors)) return errors;
  if (errors == null) return ['Action failed.'];
  return [errors];
}

// LOT-007: a lot save is only blocked when there are NO lot rows. A zero-quantity
// lot row (a valid non-negative whole number) is accepted, even if the rows sum
// to 0. Returns true when the save should be blocked as "lot code required".
export function physicalCountV2LotSaveBlocked(lotRows) {
  return !Array.isArray(lotRows) || lotRows.length === 0;
}

// IMP-005: a PCount V2 document must not be SAVED empty via a manual Save, but a
// CSV import into an empty PCount legitimately funnels through the same save()
// (deferred-creation flow) with the imported rows carried in the `csv_lines`
// upload payload — NOT yet in the grid. So the "add at least one item" guard
// must block ONLY when the grid is empty AND there is no CSV import in progress.
//
//   gridRows            : the current grid rows (tabulator.getData())
//   csvImportInProgress : any truthy value indicating a CSV/XLSX upload is
//                         underway (the component passes several independent
//                         signals: processingCsvUpload, isUploaded,
//                         processingXlsxUpload, documentStatus==='Uploading',
//                         a staged fileList entry, or a built csv_lines payload)
//
// Returns true when the empty-document save should be blocked.
export function physicalCountV2EmptySaveBlocked(gridRows, csvImportInProgress) {
  if (csvImportInProgress) return false;
  var itemCount = Array.isArray(gridRows)
    ? gridRows.filter(function (row) {
        return row && (Number(row.line_no) > 0 || row.product_id !== undefined);
      }).length
    : 0;
  return itemCount === 0;
}
